export interface ApiErrors {
  readonly errors: ApiError[];
}

export interface ApiError {
  readonly errorCode: string;
  readonly errorMessage?: string;
}

export interface ErrorState<U> {
  readonly operationType: U;
  readonly response?: ApiErrors;
  readonly status?: number;
}

export interface EntityType<T, U> {
  readonly current: null | T;
  readonly error: ErrorState<U> | null;
  readonly isLoading: boolean;
}

export interface ApiErrorOld {
  readonly detail?: string;
  readonly instance?: string;
  readonly status?: number;
  readonly title?: string;
  readonly type?: string;
}

export interface ErrorStateOld<U> {
  readonly operationType: U;
  readonly response?: ApiErrorOld;
}

export interface EntityTypeOld<T, U> {
  readonly current: null | T;
  readonly error: ErrorStateOld<U> | null;
  readonly isLoading: boolean;
}

export enum INSURANCE_NAME_VALUE {
  ACCIDENT = "ACCIDENT",
  ADVICE = "ADVICE",
  AKA_CHILD = "AKA_CHILD",
  AKA_CRITICAL_ILLNESS = "AKA_CRITICAL_ILLNESS",
  AKA_FAMILY_ACCIDENT = "AKA_FAMILY_ACCIDENT",
  AKA_GROUP_LIFE = "AKA_GROUP_LIFE",
  AKA_HEALTH = "AKA_HEALTH",
  AKA_HEALTH_WITHOUT_SIGNING = "AKA_HEALTH_WITHOUT_SIGNING",
  AKA_HOUSE_CONTENTS = "AKA_HOUSE_CONTENTS",
  AKA_STUDENT = "AKA_STUDENT",
  AKA_TRAVEL = "AKA_TRAVEL",
  ANIMAL = "ANIMAL",
  BOAT = "BOAT",
  CABIN = "CABIN",
  CABIN_CONTENTS = "CABIN_CONTENTS",
  CAR = "CAR",
  CAR_NORSK_ELBILFORENING = "AUTO_NORSK_ELBILFORENING",
  CARAVAN = "CARAVAN",
  CHILD = "CHILD",
  CHILD_HEALTH = "CHILD_HEALTH",
  CRITICAL_ILLNESS = "CRITICAL_ILLNESS",
  DEATH = "DEATH",
  DISABILITY_FLEX = "DISABILITY_FLEX",
  ELECTRIC_SCOOTER = "ELECTRIC_SCOOTER",
  HEALTH = "HEALTH",
  HEALTH_WITHOUT_SIGNING = "HEALTH_WITHOUT_SIGNING",
  HOUSE = "HOUSE",
  HOUSE_CONTENTS = "HOUSE_CONTENTS",
  HUSEIERNE_HOUSE_CONTENTS = "HUSEIERNE_HOUSE_CONTENTS",
  NEW_CHILD = "NEWCHILD",
  MOPED = "MOPED",
  MOTORCYCLE = "MOTORCYCLE",
  PREGNANT_AND_NEWBORN = "PREGNANT_AND_NEWBORN",
  RENTAL = "RENTAL",
  TRACTOR = "TRACTOR",
  SOP_SICKNESS_LEAVE = "SOP_SICKNESS_LEAVE",
  TRAILER = "TRAILER",
  TRAVEL = "TRAVEL",
  VALUABLES = "VALUABLES",
  VETERAN_AUTO = "VETERANAUTO",
  MANUAL = "MANUAL",
}

export type InsuranceName =
  | INSURANCE_NAME_VALUE.ACCIDENT
  | INSURANCE_NAME_VALUE.ANIMAL
  | INSURANCE_NAME_VALUE.AKA_CHILD
  | INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS
  | INSURANCE_NAME_VALUE.AKA_FAMILY_ACCIDENT
  | INSURANCE_NAME_VALUE.AKA_GROUP_LIFE
  | INSURANCE_NAME_VALUE.AKA_HEALTH
  | INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING
  | INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS
  | INSURANCE_NAME_VALUE.AKA_STUDENT
  | INSURANCE_NAME_VALUE.AKA_TRAVEL
  | INSURANCE_NAME_VALUE.BOAT
  | INSURANCE_NAME_VALUE.CABIN
  | INSURANCE_NAME_VALUE.CABIN_CONTENTS
  | INSURANCE_NAME_VALUE.CAR
  | INSURANCE_NAME_VALUE.CAR_NORSK_ELBILFORENING
  | INSURANCE_NAME_VALUE.CARAVAN
  | INSURANCE_NAME_VALUE.CHILD
  | INSURANCE_NAME_VALUE.CHILD_HEALTH
  | INSURANCE_NAME_VALUE.CRITICAL_ILLNESS
  | INSURANCE_NAME_VALUE.DEATH
  | INSURANCE_NAME_VALUE.DISABILITY_FLEX
  | INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER
  | INSURANCE_NAME_VALUE.HEALTH
  | INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING
  | INSURANCE_NAME_VALUE.HOUSE
  | INSURANCE_NAME_VALUE.HOUSE_CONTENTS
  | INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS
  | INSURANCE_NAME_VALUE.NEW_CHILD
  | INSURANCE_NAME_VALUE.MOPED
  | INSURANCE_NAME_VALUE.MOTORCYCLE
  | INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN
  | INSURANCE_NAME_VALUE.RENTAL
  | INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE
  | INSURANCE_NAME_VALUE.TRACTOR
  | INSURANCE_NAME_VALUE.TRAILER
  | INSURANCE_NAME_VALUE.TRAVEL
  | INSURANCE_NAME_VALUE.VALUABLES
  | INSURANCE_NAME_VALUE.VETERAN_AUTO;

export type ChildInsuranceName =
  | INSURANCE_NAME_VALUE.AKA_CHILD
  | INSURANCE_NAME_VALUE.CHILD
  | INSURANCE_NAME_VALUE.NEW_CHILD;

export const PRODUCTS_HIDE_BUY_MORE = [
  INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE,
  INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN,
];

export const PARTNER_PRODUCTS_HIDE_BUY_MORE = [
  INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER,
];

export const isRiskInsurance = (name?: INSURANCE_NAME_VALUE): boolean => {
  switch (name) {
    case INSURANCE_NAME_VALUE.ACCIDENT:
    case INSURANCE_NAME_VALUE.ANIMAL:
    case INSURANCE_NAME_VALUE.AKA_FAMILY_ACCIDENT:
    case INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.AKA_STUDENT:
    case INSURANCE_NAME_VALUE.AKA_TRAVEL:
    case INSURANCE_NAME_VALUE.BOAT:
    case INSURANCE_NAME_VALUE.CABIN:
    case INSURANCE_NAME_VALUE.CABIN_CONTENTS:
    case INSURANCE_NAME_VALUE.CAR:
    case INSURANCE_NAME_VALUE.CAR_NORSK_ELBILFORENING:
    case INSURANCE_NAME_VALUE.CARAVAN:
    case INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER:
    case INSURANCE_NAME_VALUE.HOUSE:
    case INSURANCE_NAME_VALUE.HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.MOPED:
    case INSURANCE_NAME_VALUE.MOTORCYCLE:
    case INSURANCE_NAME_VALUE.RENTAL:
    case INSURANCE_NAME_VALUE.TRACTOR:
    case INSURANCE_NAME_VALUE.TRAILER:
    case INSURANCE_NAME_VALUE.TRAVEL:
    case INSURANCE_NAME_VALUE.VALUABLES:
    case INSURANCE_NAME_VALUE.VETERAN_AUTO:
    case INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE:
      return true;
    default:
      return false;
  }
};

export const isLifeInsurance = (name: INSURANCE_NAME_VALUE): boolean => {
  switch (name) {
    case INSURANCE_NAME_VALUE.AKA_GROUP_LIFE:
    case INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS:
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
    case INSURANCE_NAME_VALUE.DEATH:
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return true;
    default:
      return false;
  }
};

export const isChildInsurance = (name?: INSURANCE_NAME_VALUE): boolean => {
  switch (name) {
    case INSURANCE_NAME_VALUE.AKA_CHILD:
    case INSURANCE_NAME_VALUE.CHILD:
    case INSURANCE_NAME_VALUE.NEW_CHILD:
      return true;
    default:
      return false;
  }
};

export const isLifeOrChildInsurance = (name: INSURANCE_NAME_VALUE): boolean =>
  isLifeInsurance(name) || isChildInsurance(name);

export const isPersonalInsurance = (name: INSURANCE_NAME_VALUE): boolean => {
  switch (name) {
    case INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN:
    case INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE:
    case INSURANCE_NAME_VALUE.AKA_HEALTH:
    case INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING:
    case INSURANCE_NAME_VALUE.HEALTH:
    case INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING:
      return true;
    default:
      return false;
  }
};

export const isStorebrandLifeProviderInsurance = (
  name: INSURANCE_NAME_VALUE,
): boolean => {
  switch (name) {
    case INSURANCE_NAME_VALUE.AKA_CHILD:
    case INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS:
    case INSURANCE_NAME_VALUE.AKA_GROUP_LIFE:
    case INSURANCE_NAME_VALUE.CHILD:
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
    case INSURANCE_NAME_VALUE.DEATH:
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
    case INSURANCE_NAME_VALUE.NEW_CHILD:
    case INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN:
      return true;
    default:
      return false;
  }
};

export const isStorebrandHealthProviderInsurance = (
  name: INSURANCE_NAME_VALUE,
): boolean => {
  switch (name) {
    case INSURANCE_NAME_VALUE.AKA_HEALTH:
    case INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING:
    case INSURANCE_NAME_VALUE.HEALTH:
    case INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING:
    case INSURANCE_NAME_VALUE.CHILD_HEALTH:
      return true;
    default:
      return false;
  }
};
